/**
 * O que faz: Página de Follow-up: cabeçalho com "Nova campanha", resultados do período e a lista
 *            de campanhas (com filtro Todas/Ativas/Pausadas). "Nova campanha" abre o assistente já
 *            na etapa "Modelo", então escolher modelo e configurar acontecem no mesmo lugar.
 * Onde usa: Rota /follow-up (FollowupPage) e aba Follow-up das Configurações.
 * Conexões: useFollowupCampaigns, FollowupCampaignMetrics, CampaignCard e FollowupWizard.
 *           Visual do Gens Design System (.gens-escopo no próprio contêiner, para funcionar também nas Configurações).
 * Tabelas: follow_up_campaigns e follow_up_templates via hooks.
 * Autor: IA
 * Data: 23/04/2026 (hierarquia e design system em 07/10/2026).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Building2, Lock, Plus, Repeat2 } from 'lucide-react';
import {
  CartaoConfiguracoes as Card,
  ConteudoCartaoConfiguracoes as CardContent,
} from '@/components/settings/layout/CartaoConfiguracoes';
import { Button } from '@/components/ui/button';
import { CabecalhoConfiguracoes } from '@/components/settings/layout/CabecalhoConfiguracoes';
import { Skeleton } from '@/components/ui/skeleton';
import { useFollowupCampaigns } from '@/hooks/useFollowupData';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useAdminClinicaPersistente } from '@/hooks/useAdminClinicaPersistente';
import { AdminClinicSelector } from '@/components/admin/AdminClinicSelector';
import { cn } from '@/lib/utils';
import { CampaignCard } from './CampaignCard';
import { FollowupCampaignMetrics } from './FollowupCampaignMetrics';
import { FollowupWizard } from './wizard/FollowupWizard';
import type { FollowupCampaign } from '@/types/followup';
// Garante os tokens do escopo também quando a tela é aberta dentro das Configurações.
import '@/styles/gens-escopo.css';

type FiltroStatusCampanha = 'todas' | 'ativas' | 'pausadas';

interface FollowupManagementProps {
  // true quando a tela aparece como aba das Configurações (troca o cabeçalho de página pelo de aba).
  emAbaDeConfiguracoes?: boolean;
}

export const FollowupManagement = ({ emAbaDeConfiguracoes = false }: FollowupManagementProps = {}) => {
  const { canUseFollowup, isLoading: planLoading, planName } = usePlanPermissions();

  // Admin escolhe qual clínica inspecionar; usuário comum permanece restrito à própria conta pelo RLS.
  const { clinicaSelecionada, selecionarClinica, isAdmin } = useAdminClinicaPersistente();
  const targetClinicaId = isAdmin ? clinicaSelecionada?.id : undefined;
  // Enquanto o admin não escolhe uma clínica, evitamos carregar dados de todas as contas misturados.
  const adminAguardandoClinica = isAdmin && !clinicaSelecionada;
  const podeUsar = canUseFollowup && !adminAguardandoClinica;

  const { data: campaigns = [], isLoading } = useFollowupCampaigns(podeUsar, targetClinicaId);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<FollowupCampaign | null>(null);
  // A origem da cópia fica separada da edição para salvar sempre uma campanha nova.
  const [duplicatingCampaign, setDuplicatingCampaign] = useState<FollowupCampaign | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<FiltroStatusCampanha>('todas');

  // Contagens dos chips de filtro; ativas primeiro na lista para quem está operando o dia a dia.
  const totalAtivas = campaigns.filter((campanha) => campanha.ativo).length;
  const totalPausadas = campaigns.length - totalAtivas;
  const campanhasVisiveis = useMemo(() => {
    const filtradas = campaigns.filter((campanha) => (
      filtroStatus === 'todas' || (filtroStatus === 'ativas' ? campanha.ativo : !campanha.ativo)
    ));
    return [...filtradas].sort((primeira, segunda) => Number(segunda.ativo) - Number(primeira.ativo));
  }, [campaigns, filtroStatus]);

  const handleNew = () => {
    if (!podeUsar) return;
    // Sem campanha em edição, o assistente abre na etapa "Modelo".
    setEditingCampaign(null);
    setDuplicatingCampaign(null);
    setWizardOpen(true);
  };

  const handleEdit = (campaign: FollowupCampaign) => {
    // Abre wizard preenchido para permitir ajuste fino sem recriar campanha do zero.
    // Motivo: preservar histórico e reduzir risco de divergência entre regras em produção.
    setEditingCampaign(campaign);
    setDuplicatingCampaign(null);
    setWizardOpen(true);
  };

  const handleDuplicate = (campaign: FollowupCampaign) => {
    // Restringe a cópia à conta selecionada; impede iniciar o fluxo sem acesso à funcionalidade.
    if (!podeUsar) return;
    setEditingCampaign(null);
    setDuplicatingCampaign(campaign);
    setWizardOpen(true);
  };

  const opcoesFiltro: { valor: FiltroStatusCampanha; rotulo: string; total: number }[] = [
    { valor: 'todas', rotulo: 'Todas', total: campaigns.length },
    { valor: 'ativas', rotulo: 'Ativas', total: totalAtivas },
    { valor: 'pausadas', rotulo: 'Pausadas', total: totalPausadas },
  ];

  // Ação principal única da tela (lima), reaproveitada nos dois formatos de cabeçalho.
  const botaoNovaCampanha = (
    <Button
      onClick={handleNew}
      disabled={!podeUsar}
      className="h-11 w-full rounded-full px-5 font-semibold hover:bg-brand-soft sm:w-auto"
    >
      <Plus className="h-4 w-4" />
      Nova campanha
    </Button>
  );

  return (
    <div className={`gens-escopo min-w-0 bg-transparent ${emAbaDeConfiguracoes ? 'space-y-5' : 'space-y-8'}`}>
      {/* Na rota /follow-up é cabeçalho de página (seção 9.13); dentro das Configurações usa o
          cabeçalho padrão das abas, para não destoar das outras seções. A ação principal é a mesma. */}
      {emAbaDeConfiguracoes ? (
        <CabecalhoConfiguracoes
          titulo="Follow-up"
          descricao="Mensagens automáticas que retomam a conversa quando o lead para de responder."
          icone={Repeat2}
          acoes={botaoNovaCampanha}
        />
      ) : (
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl leading-tight tracking-tight text-foreground sm:text-[28px]">Follow-up</h1>
            <p className="mt-1.5 max-w-[60ch] text-sm leading-relaxed text-muted-foreground">
              Mensagens automáticas que retomam a conversa quando o lead para de responder.
            </p>
          </div>
          {botaoNovaCampanha}
        </header>
      )}

      {/* Seletor de clínica exclusivo do admin, no mesmo padrão do Dashboard, para isolar os dados por conta. */}
      {isAdmin && (
        <Card>
          <CardContent semCabecalho>
            <AdminClinicSelector
              clinicaSelecionada={clinicaSelecionada}
              onClinicaSelected={selecionarClinica}
            />
          </CardContent>
        </Card>
      )}

      {!planLoading && !canUseFollowup && (
        <div role="status" className="flex items-start gap-3 rounded-3xl bg-warning-subtle p-5 text-warning-text">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-card">
            <Lock className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="font-semibold">Follow-up disponível apenas nos planos Premium e PMK.</p>
            <p className="mt-1 text-sm">
              Plano atual: {planName || 'free'}. Para usar campanhas automáticas de reativação, altere o plano da clínica.
            </p>
          </div>
        </div>
      )}

      {/* Admin precisa escolher uma clínica antes de ver métricas e campanhas, evitando dados misturados. */}
      {adminAguardandoClinica && (
        <EstadoVazioFollowup
          icone={Building2}
          titulo="Escolha uma clínica"
          texto="Selecione uma clínica acima para ver os resultados e as campanhas de follow-up dessa conta."
        />
      )}

      {podeUsar && <FollowupCampaignMetrics clinicaId={targetClinicaId} />}

      {podeUsar && (
        <section aria-labelledby="titulo-campanhas" className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 id="titulo-campanhas" className="text-lg leading-snug text-foreground">Campanhas</h2>
              <p className="mt-0.5 text-[13px] text-muted-foreground">
                {isLoading
                  ? 'Carregando campanhas...'
                  : `${totalAtivas} ${totalAtivas === 1 ? 'ativa' : 'ativas'} · ${totalPausadas} ${totalPausadas === 1 ? 'pausada' : 'pausadas'}`}
              </p>
            </div>

            {/* Chips de filtro (seção 9.6): só aparecem quando há o que filtrar. */}
            {campaigns.length > 1 && (
              <div role="group" aria-label="Filtrar campanhas por status" className="flex flex-wrap gap-2">
                {opcoesFiltro.map((opcao) => {
                  const ativo = filtroStatus === opcao.valor;
                  return (
                    <button
                      key={opcao.valor}
                      type="button"
                      aria-pressed={ativo}
                      onClick={() => setFiltroStatus(opcao.valor)}
                      className={cn(
                        'inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                        ativo
                          ? 'border-transparent bg-foreground text-background'
                          : 'border-input bg-card text-foreground hover:bg-muted',
                      )}
                    >
                      {opcao.rotulo}
                      <span className="tabular-nums opacity-75">{opcao.total}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {isLoading ? (
            // Esqueleto com a altura aproximada do card, para a página não "pular" quando os dados chegam.
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3" aria-busy="true">
              {[0, 1].map((indice) => <Skeleton key={indice} className="h-64 rounded-3xl bg-secondary" />)}
            </div>
          ) : campaigns.length === 0 ? (
            <EstadoVazioFollowup
              icone={Repeat2}
              titulo="Nenhuma campanha ainda"
              texto="Escolha um modelo pronto e, em poucos passos, o CRM passa a retomar sozinho as conversas que esfriaram."
              acao={(
                <Button onClick={handleNew} className="h-11 rounded-full px-5 font-semibold hover:bg-brand-soft">
                  <Plus className="h-4 w-4" />
                  Criar primeira campanha
                </Button>
              )}
            />
          ) : campanhasVisiveis.length === 0 ? (
            <p className="rounded-3xl bg-muted px-5 py-6 text-center text-sm text-muted-foreground">
              Nenhuma campanha {filtroStatus === 'ativas' ? 'ativa' : 'pausada'} agora.
            </p>
          ) : (
            // A key troca com o filtro: a lista nova entra com fade, sem animar altura.
            <div
              key={filtroStatus}
              className="grid grid-cols-1 gap-4 animate-in fade-in-0 duration-200 motion-reduce:animate-none md:grid-cols-2 2xl:grid-cols-3"
            >
              {campanhasVisiveis.map((campaign) => (
                <CampaignCard key={campaign.id} campaign={campaign} onEdit={handleEdit} onDuplicate={handleDuplicate} />
              ))}
            </div>
          )}
        </section>
      )}

      <FollowupWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        seedPreset={null}
        editingCampaign={editingCampaign}
        duplicatingCampaign={duplicatingCampaign}
        targetClinicaId={targetClinicaId}
        escolherModelo={!editingCampaign && !duplicatingCampaign}
      />
    </div>
  );
};

interface EstadoVazioFollowupProps {
  icone: typeof Repeat2;
  titulo: string;
  texto: string;
  acao?: ReactNode;
}

/** Estado vazio do design system (seção 9.16): pontos atrás da caixa de ícone, título, explicação e uma ação. */
const EstadoVazioFollowup = ({ icone: Icone, titulo, texto, acao }: EstadoVazioFollowupProps) => (
  <div className="flex flex-col items-center rounded-3xl border border-border bg-card px-6 py-12 text-center">
    <div className="lg-pontos mb-4 rounded-3xl p-4">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-accent-foreground">
        <Icone className="h-5 w-5" aria-hidden="true" />
      </span>
    </div>
    <h3 className="text-lg text-foreground">{titulo}</h3>
    <p className="mt-1.5 max-w-[46ch] text-sm leading-relaxed text-muted-foreground">{texto}</p>
    {acao && <div className="mt-5">{acao}</div>}
  </div>
);
